import { useEffect, useRef, useState } from 'react';
import { AnyObject } from '@triare/auth-redux';
import { useBlocker, useNavigate } from 'react-router-dom';
import { Action as HistoryAction, Location } from '@remix-run/router/history';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { useSimpleModal } from '../components/Common/Modal/Simple';

const preventReload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  Object.assign(event, {
    returnValue: true,
  });
};

export default function useUnsavedChanges() {
  const navigate = useNavigate();
  const [isChanged, setChanged] = useState(false);
  const [initData, setInitData] = useState<AnyObject>({});
  const [jsonData, setJsonData] = useState<string>('');
  const state = useRef(false);
  const { open, contextHolder } = useSimpleModal();

  useEffect(() => {
    state.current = isChanged;
  }, [isChanged]);

  useBlocker(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: Location;
      nextLocation: Location;
      historyAction: HistoryAction;
    }) => {
      if (state.current && currentLocation.pathname !== nextLocation.pathname) {
        open({
          icon: <ExclamationCircleFilled />,
          title: 'Unsaved changes',
          content: (
            <span>
              Are you sure you want to leave this page?
              <br />
              Changes you made will not be saved.
            </span>
          ),
          cancelText: 'Cancel',
          okText: 'Leave page',
          onOk: () => {
            state.current = false;
            window.removeEventListener('beforeunload', preventReload);
            navigate(nextLocation.pathname);
          },
        });

        return true;
      }

      return false;
    },
  );

  useEffect(() => {
    if (isChanged) {
      window.addEventListener('beforeunload', preventReload, false);

      return () => {
        window.removeEventListener('beforeunload', preventReload);
      };
    }

    return () => {
      // none
    };
  }, [isChanged]);

  return {
    contextHolder,
    isChanged,
    initData,
    setChanged,
    setInitData: (data: AnyObject) => {
      state.current = false;
      setInitData(data);
      setJsonData(JSON.stringify(data));
    },
    check: (data: AnyObject) => JSON.stringify(data) !== jsonData,
  };
}
